import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type VisaDirectPushTransactionInfoProps = {
  transaction: vp.VisaDirectPushTransactionResponse;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<VisaDirectPushTransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
  cardDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const VisaDirectPushTransactionInfo: FC = (props: VisaDirectPushTransactionInfoProps) => {
  const { transaction } = props;
  const { state } = useLocation<DataProps>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.VisaDirectPushTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
    });
  }, [transaction.TransactionID, currentDate]);

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('cardDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('cardDetails')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </div>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionId'),
            value: transaction.TransactionID,
            visible: true,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
            visible: true,
          },
          {
            display: t('transactionDetails.transactionDateTime'),
            value: transaction.TransactionDateTime,
            visible: true,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
            visible: true,
          },
          {
            display: t('transactionDetails.type'),
            value: 'Visa Direct Push',
            visible: true,
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
            visible: true,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
            visible: true,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
            visible: true,
          },
          {
            display: t('transactionDetails.clientReferenceNumber'),
            value: transaction.ClientReferenceNumber,
            visible: !!transaction.ClientReferenceNumber,
          },
          {
            display: t('transactionDetails.notes'),
            value: transactions.map((data) => data.Notes).pop() || '',
            visible: !!transactions.map((data) => data.Notes).pop(),
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountInfoProps;
      },
      cardDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('cardDetails.name'),
            value: `${transaction.FirstName} ${transaction.LastName}`,
          },
          {
            display: t('cardDetails.cardNumber'),
            value: transaction.MaskedCardNumber,
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'card-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('cardDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return accountInfoProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default VisaDirectPushTransactionInfo;
